import React from "react";
import { motion } from "framer-motion";
import { Check, TrendingUp, Globe, Award, Rocket, Brain, GraduationCap } from "lucide-react";
import { CambridgeSummerConfig, CambridgeSummerWhyJoinReason } from "../../../data/cambridgeSummerConfig";

interface ProgramOverviewProps {
    config: CambridgeSummerConfig;
}

const ProgramOverview: React.FC<ProgramOverviewProps> = ({ config }) => {
    const { whyJoin, benefits } = config;

    const iconMap: Record<string, React.ElementType> = {
        TrendingUp,
        Globe,
        Award,
        Rocket,
        Brain,
        GraduationCap,
    };

    return (
        <section className="py-16 md:py-24 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Why Join Section */}
                <div className="mb-20">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <div className="inline-flex items-center px-4 py-2 rounded-full border border-[#003C71] mb-6">
                            <Rocket className="w-4 h-4 text-[#003C71] mr-2" />
                            <span className="text-[#003C71] font-normal text-xs uppercase">Key Benefits for Students</span>
                        </div>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#111827] mb-4">
                            {whyJoin.title}
                        </h2>
                        <p className="text-xl text-[#4B5563] font-normal max-w-3xl mx-auto">
                            {whyJoin.subheadline}
                        </p>
                    </motion.div>

                    {/* 2-Week Programme Reasons */}
                    <div className="mb-12">
                        <motion.h3
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                            className="text-2xl md:text-3xl font-bold text-[#003C71] mb-8 text-center"
                        >
                            Why Join the 2-Week Programme?
                        </motion.h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {whyJoin.twoWeekReasons.map((reason: CambridgeSummerWhyJoinReason, index: number) => {
                                const IconComponent = iconMap[reason.icon] || Rocket;
                                return (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 40 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: index * 0.1 }}
                                        viewport={{ once: true }}
                                        className="group relative"
                                    >
                                        <div className="relative h-full">
                                            <div className="absolute -inset-0.5 bg-gradient-to-r from-[#003C71] via-[#0066CC] to-[#9FCEF5] rounded-2xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-500" />
                                            <div className="relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden h-full border border-gray-100 group-hover:border-transparent">
                                                {reason.image && (
                                                    <div className="relative h-48 overflow-hidden">
                                                        <img
                                                            src={reason.image}
                                                            alt={reason.title}
                                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                                        />
                                                        <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-black/10 to-transparent" />
                                                        <div className="absolute bottom-4 left-4 w-14 h-14 bg-[#003C71] rounded-xl flex items-center justify-center shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                                                            <IconComponent className="w-7 h-7 text-white" />
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="p-4">
                                                    <h3 className="text-[15px] font-bold text-[#111827] mb-3 group-hover:text-[#003C71] transition-colors">
                                                        {reason.title}
                                                    </h3>
                                                    <p className="text-xs text-[#4B5563] font-normal leading-relaxed">
                                                        {reason.description}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>

                    {/* 4-Week Programme Reasons */}
                    <div className="mb-12">
                        <motion.h3
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                            className="text-2xl md:text-3xl font-bold text-[#003C71] mb-8 text-center"
                        >
                            Why Join the 4-Week Programme?
                        </motion.h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {whyJoin.fourWeekReasons.map((reason: CambridgeSummerWhyJoinReason, index: number) => {
                                const IconComponent = iconMap[reason.icon] || Rocket;
                                return (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 40 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: index * 0.1 }}
                                        viewport={{ once: true }}
                                        className="group relative"
                                    >
                                        <div className="relative h-full">
                                            <div className="absolute -inset-0.5 bg-gradient-to-r from-[#8B1538] via-[#A51C30] to-[#003C71] rounded-2xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-500" />
                                            <div className="relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden h-full border border-gray-100 group-hover:border-transparent">
                                                {reason.image && (
                                                    <div className="relative h-48 overflow-hidden">
                                                        <img
                                                            src={reason.image}
                                                            alt={reason.title}
                                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                                        />
                                                        <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-black/10 to-transparent" />
                                                        <div className="absolute bottom-4 left-4 w-14 h-14 bg-[#8B1538] rounded-xl flex items-center justify-center shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                                                            <IconComponent className="w-7 h-7 text-white" />
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="p-4">
                                                    <h3 className="text-[15px] font-bold text-[#111827] mb-3 group-hover:text-[#8B1538] transition-colors">
                                                        {reason.title}
                                                    </h3>
                                                    <p className="text-xs text-[#4B5563] font-normal leading-relaxed">
                                                        {reason.description}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Benefits Section */}
                <div className="bg-gradient-to-br from-[#003C71] to-[#0066CC] rounded-3xl p-8 md:p-12 lg:p-16">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                            {benefits.title}
                        </h2>
                        <p className="text-lg text-white/80 max-w-2xl mx-auto">
                            Students leave with both tangible credentials and measurable personal growth.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                        {benefits.items.map((item, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.05 }}
                                viewport={{ once: true }}
                                className="flex items-start space-x-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-all duration-300"
                            >
                                <div className="flex-shrink-0 w-6 h-6 bg-[#9FCEF5] rounded-full flex items-center justify-center mt-1">
                                    <Check className="w-4 h-4 text-[#003C71]" />
                                </div>
                                <p className="text-white text-base leading-relaxed flex-1">
                                    {item.text}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ProgramOverview;
